import { Component } from '@theme/component';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const desktopQuery = window.matchMedia('(min-width: 990px)');

/** @type {Set<TmCardSlides>} */
const pending = new Set();

/** @type {IntersectionObserver | null} */
let viewportObserver = null;

/**
 * Shared observer on the page scroll container (`.page-wrapper` from 990 px, the viewport
 * below): inserts worn photos once a card is on screen. Rebuilt when that breakpoint flips.
 */
function connectObserver() {
  viewportObserver?.disconnect();
  if (!('IntersectionObserver' in window)) {
    viewportObserver = null;
    for (const element of pending) element.hydrate();
    return;
  }

  viewportObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target instanceof TmCardSlides) entry.target.hydrate();
      }
    },
    { root: getIntersectionRoot() }
  );
  for (const element of pending) viewportObserver.observe(element);
}

scrollContainerMediaQuery.addEventListener('change', connectObserver);

/**
 * Swipeable packshot → worn slides of a v8 product card (tm-product-card snippet).
 * - Inserts the deferred worn photo (`template[data-worn]`) when the card enters the
 *   viewport or on first pointer, touch or focus, so it never competes with the LCP image.
 * - Mirrors the visible slide on `.tm-card__media` as `data-slide` for the CSS dots.
 * Desktop swaps to the worn photo on hover in CSS only.
 */
class TmCardSlides extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('scroll', this.#onScroll, { passive: true, signal });

    const card = this.closest('product-card') ?? this;
    for (const type of ['pointerenter', 'touchstart', 'focusin']) {
      card.addEventListener(type, this.hydrate, { passive: true, once: true, signal });
    }

    if (this.querySelector('template[data-worn]')) {
      pending.add(this);
      if (!viewportObserver) connectObserver();
      viewportObserver?.observe(this);
    }

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    pending.delete(this);
    viewportObserver?.unobserve(this);
    cancelAnimationFrame(this.#frame);
  }

  /** Inserts the deferred worn photo once. `data-worn-desktop`: only from 990 px (athlete rail). */
  hydrate = () => {
    if (this.hasAttribute('data-worn-desktop') && !desktopQuery.matches) return;
    const media = this.closest('.tm-card__media, .tmc-card__media');
    if (!(media instanceof HTMLElement) || media.hasAttribute('data-worn-ready')) return;

    const template = this.querySelector('template[data-worn]');
    if (!(template instanceof HTMLTemplateElement)) return;

    template.replaceWith(template.content.cloneNode(true));
    media.setAttribute('data-worn-ready', '');
    pending.delete(this);
    viewportObserver?.unobserve(this);
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const media = this.closest('.tm-card__media, .tmc-card__media');
    if (!(media instanceof HTMLElement) || this.clientWidth === 0) return;

    const index = Math.round(Math.abs(this.scrollLeft) / this.clientWidth);
    media.dataset.slide = String(index);
  };
}

if (!customElements.get('tm-card-slides')) {
  customElements.define('tm-card-slides', TmCardSlides);
}
